import { ethers, Contract, BrowserProvider, JsonRpcProvider, Signer, ZeroAddress } from "ethers";

import {
    CERTIFICATE_CONTRACT_ADDRESS,
    CERTIFICATE_ABI,
    TRACKER_CONTRACT_ADDRESS,
    TRACKER_ABI,
    IPFS_GATEWAY_PREFIX,
    RPC_URL
} from "../constants";
import { toGatewayUrl } from "./ipfsGateway";

interface WalletConnectionResult {
    address: string;
    networkName: string;
    chainId: string;
}

let provider: BrowserProvider | undefined;
let signer: Signer | undefined;
let certificateContract: Contract | undefined;
let trackerContract: Contract | undefined;

export const connectWallet = async (): Promise<WalletConnectionResult> => {
    if (window.ethereum) {
        try {
            provider = new ethers.BrowserProvider(window.ethereum);
            await provider.send("eth_requestAccounts", []);
            signer = await provider.getSigner();

            certificateContract = new ethers.Contract(CERTIFICATE_CONTRACT_ADDRESS, CERTIFICATE_ABI, signer);
            trackerContract = new ethers.Contract(TRACKER_CONTRACT_ADDRESS, TRACKER_ABI, signer);
            const address = await signer.getAddress();
            const network = await provider.getNetwork();
            return { address, networkName: network.name, chainId: network.chainId.toString() } as WalletConnectionResult;
        } catch (error) {
            console.error("Failed to connect wallet:", error);
            let message = "An unknown error occurred.";
            if (error instanceof Error) {
                message = error.message;
            }
            throw new Error("Failed to connect wallet. " + message);
        }
    } else {
        throw new Error("MetaMask not detected. Please install MetaMask.");
    }
};

export const getSignerAddress = async (): Promise<string> => {
    if (!signer) throw new Error("Wallet not connected");
    return await signer.getAddress();
};

export const getCertificateContractOwner = async (): Promise<string> => {
    // Try with the signer-based contract instance first, if available and its provider is set
    if (certificateContract && certificateContract.runner && certificateContract.runner.provider) {
        try {
            const ownerAddress = await certificateContract.owner();
            // Basic validation for the returned address
            if (ethers.isAddress(ownerAddress) && ownerAddress !== ZeroAddress) {
                return ownerAddress;
            }
            console.warn("Signer-based owner() call returned invalid address:", ownerAddress);
        } catch (e) {
            console.warn("Error calling owner() on signer-based contract, falling back to read-only provider.", e);
        }
    }

    // Fallback to a new read-only provider instance directly targeting the Hardhat node
    // This is useful if called before the wallet is fully connected/signer is ready,
    // or if the signer-based call failed or returned an invalid address.
    console.log("Using read-only provider for getCertificateContractOwner check.");
    const readProvider = new JsonRpcProvider(RPC_URL);
    const contract = new ethers.Contract(CERTIFICATE_CONTRACT_ADDRESS, CERTIFICATE_ABI, readProvider);
    try {
        return await contract.owner();
    } catch (error) {
        console.error("Error calling owner() with read-only provider:", error);
        throw error; // Re-throw the error to be caught by the caller
    }
};

export const getTrackerContractOwner = async (): Promise<string> => {
    if (trackerContract && trackerContract.runner && trackerContract.runner.provider) {
        try {
            const ownerAddress = await trackerContract.owner();
            if (ethers.isAddress(ownerAddress) && ownerAddress !== ZeroAddress) {
                return ownerAddress;
            }
            console.warn("Signer-based tracker owner() call returned invalid address:", ownerAddress);
        } catch (e) {
            console.warn("Error calling owner() on signer-based tracker contract, falling back to read-only provider.", e);
        }
    }
    console.log("Using read-only provider for getTrackerContractOwner check.");
    const readProvider = new JsonRpcProvider(RPC_URL);
    const contract = new ethers.Contract(TRACKER_CONTRACT_ADDRESS, TRACKER_ABI, readProvider);
    try {
        return await contract.owner();
    } catch (error) {
        console.error("Error calling tracker owner() with read-only provider:", error);
        throw error;
    }
};

export const mintCertificateNFT = async (
    craftsman: string,
    name: string,
    description: string,
    imageIPFSHash: string,
    tokenMetadataURI: string
): Promise<{ tokenId: string; txHash: string }> => {
    if (!certificateContract || !signer) throw new Error("Wallet not connected or contract not initialized.");
    try {
        const tx = await certificateContract.mintCertificate(craftsman, name, description, imageIPFSHash, tokenMetadataURI);
        const receipt = await tx.wait();
        const txHash = receipt.hash;
        
        // console.log("Mint transaction receipt:", receipt);
        const event = receipt?.logs?.find((log: any) => {
            try {
                const parsedLog = certificateContract!.interface.parseLog(log);
                return parsedLog && parsedLog.name === "Transfer" && parsedLog.args.from === ZeroAddress;
            } catch (e) { return false; }
        });
        if (event) {
            const parsedLog = certificateContract!.interface.parseLog(event);
            if (parsedLog && parsedLog.args && typeof parsedLog.args.tokenId !== 'undefined' && parsedLog.args.tokenId !== null) {
                return { tokenId: parsedLog.args.tokenId.toString(), txHash };
            } else {
                throw new Error("TokenId not found or invalid in mint transaction logs (parsedLog is invalid or tokenId missing).");
            }
        }
        throw new Error("TokenId not found in mint transaction logs.");
    } catch (error) {
        console.error("Error minting certificate:", error);
        throw error;
    }
};

export interface CertificateData {
    name: string;
    description: string;
    image: string | null;
    attributes: { trait_type: string; value: string | number }[];
}
export interface StatusHistoryEntry {
    statusName: string;
    timestamp: number;
    notes: string;
    latitude?: string; // Optional for now, make required if always present
    longitude?: string; // Optional for now
}
export interface ItemData {
    id: string;
    certificate: CertificateData;
    statusHistory: StatusHistoryEntry[];
    initialCertificateURI: string;
    currentCertificateURI: string;
    historicalCertificateURIs: string[];
}
export const getItemDetails = async (tokenId: string): Promise<ItemData> => {
    // Use a public provider for read-only operations if no wallet connected
    // Use the local Hardhat node URL as a fallback
    const currentProvider = signer?.provider || new JsonRpcProvider(RPC_URL);
    const certContract = new ethers.Contract(CERTIFICATE_CONTRACT_ADDRESS, CERTIFICATE_ABI, currentProvider);
    const trackContract = new ethers.Contract(TRACKER_CONTRACT_ADDRESS, TRACKER_ABI, currentProvider);

    try {
        const metadataURI = await certContract.tokenURI(tokenId);
        if (!metadataURI) throw new Error("Token URI not found.");
        const initialMetadataURI = await certContract.showInitialCertificate(tokenId);
        /* The line `const currentMetadataURI = await certContract.showCurrentCertificate(tokenId);` is
        calling a function `showCurrentCertificate` on the `certContract` object to retrieve the
        current certificate URI associated with a specific token ID. This function is likely part of
        the smart contract's functionality and is used to fetch the URI that points to the metadata
        of the current certificate linked to the token ID. */
        // const currentMetadataURI = await certContract.showCurrentCertificate(tokenId);
        const metadataResponse = await fetch(toGatewayUrl(metadataURI));
        if (!metadataResponse.ok) throw new Error("Failed to fetch metadata from IPFS.");
        const metadata = await metadataResponse.json();

        const history = await trackContract.getItemStatusHistoryWithNames(tokenId);

        const historicalURIs = await certContract.getHistoricalCertificateURIs(tokenId);


        return {
            id: tokenId,
            certificate: {
                name: metadata.name,
                description: metadata.description,
                image: metadata.image ? toGatewayUrl(metadata.image) : null,
                attributes: metadata.attributes || [],
            },
            statusHistory: history.map((h: any) => ({
                statusName: h.statusName,
                timestamp: Number(h.timestamp),
                notes: h.notes,
                latitude: h.latitude || "", // Ensure these fields exist or provide defaults
                longitude: h.longitude || ""
            })),
            initialCertificateURI: initialMetadataURI ? toGatewayUrl(initialMetadataURI) : "",
            currentCertificateURI: toGatewayUrl(metadataURI),
            historicalCertificateURIs: historicalURIs.map((uri: string) => toGatewayUrl(uri))
        };
    } catch (error) {
        console.error("Error fetching item details:", error);
        throw error;
    }
};

export const fetchCertificateFromURI = async (uri: string): Promise<CertificateData> => {
    const gatewayUrl = toGatewayUrl(uri);
    if (gatewayUrl === uri && !uri.startsWith(IPFS_GATEWAY_PREFIX)) {
        // This might be a direct link. We'll try to fetch it as is.
        console.warn(`Certificate URI "${uri}" is not a standard IPFS or recognized gateway URL. Attempting to fetch directly.`);
    }

    const response = await fetch(gatewayUrl);
    if (!response.ok) {
        throw new Error(`Failed to fetch metadata from ${gatewayUrl}. Status: ${response.status}`);
    }
    const metadata = await response.json();

    // Ensure the image URL within the metadata is also a gateway link for rendering
    if (metadata.image) {
        metadata.image = toGatewayUrl(metadata.image);
    }

    // Ensure the returned object conforms to the CertificateData interface
    return {
        name: metadata.name || "No Name",
        description: metadata.description || "No Description",
        image: metadata.image || null,
        attributes: metadata.attributes || [],
    };
};

export const updateItemStatusOnChain = async (tokenId: string, statusEnumValue: number, notes: string, latitude: string, longitude: string): Promise<string> => {
    if (!trackerContract || !signer) throw new Error("Wallet not connected or contract not initialized.");
    // Ensure statusEnumValue is a number (0 for Produced, 1 for InTransit, 2 for InStore)
    const tx = await trackerContract.updateItemStatusWithLocation(tokenId, statusEnumValue, notes, latitude, longitude);
    const receipt = await tx.wait();
    return receipt.hash;
};

export const updateCertificateOnChain = async (tokenId: string, newTokenURI: string): Promise<string> => {
    if (!certificateContract || !signer) throw new Error("Wallet not connected or contract not initialized.");
    try {
        const tx = await certificateContract.updateCertificate(tokenId, newTokenURI);
        const receipt = await tx.wait();
        return receipt.hash;
    } catch (error) {
        console.error("Error updating certificate:", error);
        throw error;
    }
};

export const getInitialCertificateURI = async (tokenId: string): Promise<string> => {
    const currentProvider = signer?.provider || new JsonRpcProvider(RPC_URL);
    const certContract = new ethers.Contract(CERTIFICATE_CONTRACT_ADDRESS, CERTIFICATE_ABI, currentProvider);
    try {
        const initialURI = await certContract.showInitialCertificate(tokenId);
        if (!initialURI) throw new Error("Initial Token URI not found.");
        return initialURI;
    } catch (error) {
        console.error(`Error fetching initial URI for token ${tokenId}:`, error);
        throw error;
    }
};


// In: frontend/src/services/web3Service.ts
// (You will need to add this function to your existing file)

// ... (your existing provider setup and other functions)

/**
 * Prompts the user to sign a message with their connected wallet.
 * @param message The message string to sign.
 * @returns A promise that resolves to the signature string.
 */
export const signMessage = async (message: string): Promise<string> => {
    // This assumes you have a way to get the ethers provider,
    // similar to your other web3 functions.
    // const provider = new ethers.providers.Web3Provider(window.ethereum);
    // const signer = provider.getSigner();
    const provider = new JsonRpcProvider(RPC_URL);

    const signer = await provider.getSigner(0);
    return await signer.signMessage(message);
};